import type { I18n } from '@learnforge/i18n';
import { createSvgEl } from '@learnforge/demo-render';
import { niceTickStep, niceTickStepPi } from '@learnforge/demo-math/numeric';

export type TickMode = 'pi' | 'decimal';

function formatTickValue(v: number): string {
  // If v is a multiple of π/k for small k, render symbolically.
  const piRatio = v / Math.PI;
  for (const den of [1, 2, 3, 4, 6, 12]) {
    const num = piRatio * den;
    const rounded = Math.round(num);
    if (Math.abs(num - rounded) < 1e-6) {
      if (rounded === 0) return '0';
      const sign = rounded < 0 ? '−' : '';
      const absN = Math.abs(rounded);
      if (den === 1) {
        if (absN === 1) return `${sign}π`;
        return `${sign}${absN}π`;
      }
      if (absN === 1) return `${sign}π/${den}`;
      return `${sign}${absN}π/${den}`;
    }
  }
  // Fall back to a 2-decimal numeric label, U+2212 minus for negatives (never −0).
  const trimmed = Math.abs(v)
    .toFixed(2)
    .replace(/\.?0+$/, '');
  return v < 0 && trimmed !== '0' ? `−${trimmed}` : trimmed;
}

export interface AxesOptions {
  xDomain: [number, number];
  yDomain: [number, number];
  /** Explicit tick steps. When omitted the step is derived from the domain via the tick mode. */
  xTickStep?: number;
  yTickStep?: number;
  /** 'pi' picks steps from π/12 … 2π, 4π …; 'decimal' (default) from the 1/2/5 family. */
  xTickMode?: TickMode;
  yTickMode?: TickMode;
  /** Target tick count for automatic steps (default 10), per axis if an object. */
  targetTicks?: number | { x: number; y: number };
  /** Multiplies tick length and label offsets so chrome stays screen-stable under zoom (pass 1/zoom). */
  chromeScale?: number;
  showGrid?: boolean;
  xLabelKey?: string;
  yLabelKey?: string;
  i18n: I18n<string>;
}

export interface AxesHandle {
  group: SVGGElement;
  update(opts: Partial<AxesOptions>): void;
  destroy(): void;
}

/** Tick positions on the `step` grid inside `domain` (inclusive, with float slack). */
export function tickValues(domain: [number, number], step: number): number[] {
  const i0 = Math.ceil(domain[0] / step - 1e-9);
  const i1 = Math.floor(domain[1] / step + 1e-9);
  const out: number[] = [];
  for (let i = i0; i <= i1; i++) out.push(i * step);
  return out;
}

function resolveStep(
  domain: [number, number],
  explicit: number | undefined,
  mode: TickMode,
  target: number,
): number {
  if (explicit !== undefined) return explicit;
  const range = domain[1] - domain[0];
  return mode === 'pi' ? niceTickStepPi(range, target) : niceTickStep(range, target);
}

export function createAxes(opts: AxesOptions): AxesHandle {
  const group = createSvgEl('g', { class: 'tg-axes' });
  let current: AxesOptions = { ...opts };

  function render() {
    while (group.firstChild) group.removeChild(group.firstChild);
    const { xDomain, yDomain, showGrid = true, chromeScale = 1 } = current;
    const target = current.targetTicks ?? 10;
    const targetX = typeof target === 'number' ? target : target.x;
    const targetY = typeof target === 'number' ? target : target.y;
    const xStep = resolveStep(xDomain, current.xTickStep, current.xTickMode ?? 'decimal', targetX);
    const yStep = resolveStep(yDomain, current.yTickStep, current.yTickMode ?? 'decimal', targetY);
    const xs = tickValues(xDomain, xStep);
    const ys = tickValues(yDomain, yStep);
    const tick = 0.1 * chromeScale;
    const labelGap = 0.3 * chromeScale;

    if (showGrid) {
      const grid = createSvgEl('g', { class: 'tg-grid' });
      for (const x of xs) {
        grid.appendChild(
          createSvgEl('line', {
            x1: x,
            x2: x,
            y1: yDomain[0],
            y2: yDomain[1],
            'vector-effect': 'non-scaling-stroke',
          }),
        );
      }
      for (const y of ys) {
        grid.appendChild(
          createSvgEl('line', {
            x1: xDomain[0],
            x2: xDomain[1],
            y1: y,
            y2: y,
            'vector-effect': 'non-scaling-stroke',
          }),
        );
      }
      group.appendChild(grid);
    }

    const xAxis = createSvgEl('line', {
      class: 'tg-axis',
      x1: xDomain[0],
      x2: xDomain[1],
      y1: 0,
      y2: 0,
      'vector-effect': 'non-scaling-stroke',
    });
    const yAxis = createSvgEl('line', {
      class: 'tg-axis',
      x1: 0,
      x2: 0,
      y1: yDomain[0],
      y2: yDomain[1],
      'vector-effect': 'non-scaling-stroke',
    });
    group.append(xAxis, yAxis);

    for (const x of xs) {
      group.appendChild(
        createSvgEl('line', {
          class: 'tg-tick-x',
          x1: x,
          x2: x,
          y1: -tick,
          y2: tick,
          'vector-effect': 'non-scaling-stroke',
        }),
      );
      if (Math.abs(x) > 1e-9) {
        const label = createSvgEl('text', {
          class: 'tg-tick-label',
          x,
          y: labelGap,
          'text-anchor': 'middle',
          transform: 'scale(1 -1)',
        });
        label.textContent = formatTickValue(x);
        group.appendChild(label);
      }
    }
    for (const y of ys) {
      group.appendChild(
        createSvgEl('line', {
          class: 'tg-tick-y',
          x1: -tick,
          x2: tick,
          y1: y,
          y2: y,
          'vector-effect': 'non-scaling-stroke',
        }),
      );
      if (Math.abs(y) > 1e-9) {
        const label = createSvgEl('text', {
          class: 'tg-tick-label',
          x: -labelGap,
          y: -y,
          'text-anchor': 'end',
          'dominant-baseline': 'middle',
          transform: 'scale(1 -1)',
        });
        label.textContent = formatTickValue(y);
        group.appendChild(label);
      }
    }
  }

  render();

  return {
    group,
    update(next) {
      current = { ...current, ...next };
      render();
    },
    destroy() {
      group.remove();
    },
  };
}
